import {
  HelpCircleIcon,
  LogOutIcon,
  Undo2Icon,
  XIcon,
} from 'lucide-react'
import { type ComponentProps, forwardRef, useState } from 'react'

import { useLogout } from 'components/hooks/useLogout'
import MemberOnboardingSupportContent from 'components/MemberOnboardingSupportContent'
import { DropdownMenu, Modal, ProfileMenuToggle, SolidCircleCheckIcon } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import { useOnboarding } from '../OnboardingContext'
import { type Step } from '../types'

type ProfileMenuTriggerProps = Omit<
  ComponentProps<typeof ProfileMenuToggle>,
  'children'
> & {
  memberName?: string;
}

const ProfileMenuTrigger = forwardRef<
  HTMLButtonElement,
  ProfileMenuTriggerProps
>(({ width, memberName: _memberName, ...props }, forwardedRef) => {
  const { onboardingData } = useOnboarding()

  const memberOrEmployeeName = onboardingData
    ? onboardingData.member?.given_name && onboardingData.member?.family_name
      ? `${onboardingData.member.given_name} ${onboardingData.member.family_name}`
      : `${onboardingData.employee?.first_name} ${onboardingData.employee?.last_name}`
    : ''

  return (
    <ProfileMenuToggle
      {...props}
      data-testid='profile-menu-toggle'
      width={width}
      ref={forwardedRef}
    >
      {memberOrEmployeeName}
    </ProfileMenuToggle>
  )
})

const ProfileMenuTriggerWithoutContext = forwardRef<
  HTMLButtonElement,
  ProfileMenuTriggerProps
>(({ width, memberName, ...props }, forwardedRef) => {
  return (
    <ProfileMenuToggle
      {...props}
      data-testid='profile-menu-toggle-empty'
      width={width}
      ref={forwardedRef}
    >
      {memberName ?? ' '}
    </ProfileMenuToggle>
  )
})

export function ProfileMenu ({
  navigateToSpecificStep,
  showHelpAndSupport,
  showOptedOut,
  showReturnHome,
  withoutContext,
  memberName,
  width,
}: {
  navigateToSpecificStep?: (step: Step) => void;
  showHelpAndSupport?: boolean;
  showOptedOut?: boolean;
  showReturnHome?: boolean;
  width?: 'full' | 'auto';
  withoutContext?: boolean;
  memberName?: string;
}) {
  const { mutate: logout } = useLogout()
  const { assign } = useWindowLocation()
  const [supportModalOpen, setSupportModalOpen] = useState(false)

  const Trigger = withoutContext
    ? ProfileMenuTriggerWithoutContext
    : ProfileMenuTrigger

  return (
    <>
      <DropdownMenu trigger={<Trigger memberName={memberName} width={width} />}>
        <DropdownMenu.Section>
          <DropdownMenu.Item disabled onSelect={() => {}}>
            <div className='flex items-center gap-2'>
              <SolidCircleCheckIcon aria-hidden size={12} className='text-green-400' />
              <span className='caption text-gray-500'>All progress autosaved</span>
            </div>
          </DropdownMenu.Item>
        </DropdownMenu.Section>
        <DropdownMenu.Section>
          {showHelpAndSupport && (
            <DropdownMenu.Item
              leftIcon={HelpCircleIcon}
              onSelect={() => setSupportModalOpen(true)}
            >
              Help & support
            </DropdownMenu.Item>
          )}
          {showReturnHome && (
            <DropdownMenu.Item
              onSelect={() => assign('/')}
              leftIcon={Undo2Icon}
            >
              Return home
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item onSelect={() => logout()} leftIcon={LogOutIcon}>
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Section>
        {!withoutContext && showOptedOut && (
          <DropdownMenu.Section>
            <DropdownMenu.Item
              onSelect={
                navigateToSpecificStep
                  ? () => navigateToSpecificStep('opted_out')
                  : undefined
              }
              leftIcon={XIcon}
              type='destructive'
            >
              Decline coverage
            </DropdownMenu.Item>
          </DropdownMenu.Section>
        )}
      </DropdownMenu>
      {showHelpAndSupport && (
        <Modal open={supportModalOpen} onOpenChange={setSupportModalOpen}>
          <Modal.Body>
            <MemberOnboardingSupportContent
              onDismiss={() => setSupportModalOpen(false)}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}
